import { computed, readonly } from 'vue'
import { useState, useCookie } from '#app'
import type { User } from '~/types/user'

export const useUser = () => {
  // User state
  const user = useState<User | null>('user', () => null)
  const isLoading = useState<boolean>('user.loading', () => false)

  // Favorites state
  const favoriteIds = useState<number[]>('user.favorites', () => [])
  const favoriteIdsLoaded = useState<boolean>('user.favorites.loaded', () => false)

  // Likes state
  const likeIds = useState<number[]>('user.likes', () => [])
  const likeIdsLoaded = useState<boolean>('user.likes.loaded', () => false)

  // Token management
  const setToken = (token: string) => {
    const tokenCookie = useCookie<string | null>('auth-token', {
      secure: true,
      httpOnly: false,
      sameSite: 'strict',
      maxAge: 60 * 60 * 24 * 7, // 7 days
      default: () => null
    })
    tokenCookie.value = token
  }

  const getToken = () => {
    const tokenCookie = useCookie<string | null>('auth-token', {
      default: () => null
    })
    return tokenCookie.value
  }

  const clearToken = () => {
    const tokenCookie = useCookie<string | null>('auth-token', {
      default: () => null
    })
    tokenCookie.value = null
  }

  const refreshToken = async (): Promise<string | null> => {
    const token = getToken()
    if (!token) {
      throw new Error('No token available for refresh')
    }

    try {
      const { $api } = useNuxtApp()
      const response = await $api<{
        token?: string
        data?: { token: string; token_type: string; user?: User }
        status?: string
        message?: string
      }>('/refresh-token', {
        method: 'POST'
      })

      // Check for success status and get token from correct location
      if (response.status === 'success') {
        const newToken = response.token || response.data?.token

        if (newToken) {
          setToken(newToken)

          // If user data is provided, update it
          if (response.data?.user) {
            setUser(response.data.user)
          }

          return newToken
        } else {
          clearToken()
          clearUser()
          return null
        }
      }

      // Check for error status
      if (response.status === 'error') {
        clearToken()
        clearUser()
        throw new Error(response.message || 'Token refresh failed')
      }

      // No explicit status - try to get token anyway (backward compatibility)
      const newToken = response.token || response.data?.token
      if (newToken) {
        setToken(newToken)
        if (response.data?.user) {
          setUser(response.data.user)
        }
        return newToken
      }

      // No token received - clear current token
      clearToken()
      clearUser()
      return null
    } catch (error: unknown) {
      clearToken()
      clearUser()

      // Re-throw with more context
      const err = error as { status?: number; message?: string }
      if (err.status === 401) {
        throw new Error('Refresh token expired or invalid')
      } else if (err.message) {
        throw error
      } else {
        throw new Error('Token refresh failed')
      }
    }
  }

  // User management
  const setUser = (userData: User) => {
    user.value = userData
  }

  const clearUser = () => {
    user.value = null
    favoriteIds.value = []
    favoriteIdsLoaded.value = false
    likeIds.value = []
    likeIdsLoaded.value = false
    clearToken()
  }

  // Favorites management
  const initFavoriteIds = async () => {
    if (favoriteIdsLoaded.value) return

    const token = getToken()
    if (!token || !user.value) return

    try {
      const { $api } = useNuxtApp()
      const response = await $api<{ status: string; message: string; data: number[] }>(
        '/get-favorite-ids',
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      if (response?.status === 'success') {
        favoriteIds.value = response.data
        favoriteIdsLoaded.value = true
      }
    } catch (error) {
      console.error('Error loading favorite IDs:', error)
    }
  }

  const resetFavoriteIds = () => {
    favoriteIdsLoaded.value = false
  }

  const isFavorite = computed(() => (newsId: number): boolean => {
    return favoriteIds.value.includes(newsId)
  })

  const addFavoriteId = (newsId: number) => {
    if (!favoriteIds.value.includes(newsId)) {
      favoriteIds.value.push(newsId)
    }
  }

  const removeFavoriteId = (newsId: number) => {
    favoriteIds.value = favoriteIds.value.filter((id) => id !== newsId)
  }

  // Likes management
  const initLikeIds = async () => {
    if (likeIdsLoaded.value) return

    try {
      const { $api } = useNuxtApp()
      const token = getToken()
      const headers: Record<string, string> = {}
      let url = '/get-like-ids'

      if (token) {
        headers.Authorization = `Bearer ${token}`
      } else {
        const fingerprintStore = useFingerprintStore()
        if (!fingerprintStore.loaded) {
          await fingerprintStore.initializeFingerprint()
        }
        if (fingerprintStore.visitorId) {
          url += `?fingerprint_id=${fingerprintStore.visitorId}`
        }
      }

      const response = await $api<{ status: string; message: string; data: number[] }>(url, {
        headers
      })

      if (response?.status === 'success') {
        likeIds.value = response.data
        likeIdsLoaded.value = true
      }
    } catch (error) {
      likeIdsLoaded.value = false
      console.error('Error loading like IDs:', error)
    }
  }

  const resetLikeIds = () => {
    likeIdsLoaded.value = false
  }

  const isLiked = computed(() => (newsId: number): boolean => {
    return likeIds.value.includes(newsId)
  })

  const addLikeId = (newsId: number) => {
    if (!likeIds.value.includes(newsId)) {
      likeIds.value.push(newsId)
    }
  }

  const removeLikeId = (newsId: number) => {
    likeIds.value = likeIds.value.filter((id) => id !== newsId)
  }

  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  // Auto-login initialization
  const initializeAuth = async () => {
    const token = getToken()

    if (token && !user.value && !isLoading.value) {
      try {
        setLoading(true)
        const { $api } = useNuxtApp()
        const response = await $api<{ data: User; status?: string }>('/user')

        if (response.status === 'success' && response.data) {
          setUser(response.data)
          await initFavoriteIds()
        } else if (response.data) {
          setUser(response.data)
          await initFavoriteIds()
        } else {
          clearToken()
        }
      } catch (error: unknown) {
        // If 401, token is invalid - clear it
        if ((error as { status?: number }).status === 401) {
          clearToken()
          clearUser()
        }
      } finally {
        setLoading(false)
      }
    }
  }

  const hasPermission = (key: string) => {
    return user.value?.permissions?.includes(key) ?? false
  }

  // Computed properties
  const isLoggedIn = computed(() => !!user.value)
  const userName = computed(() => {
    if (!user.value) return 'User'
    const parts: string[] = []
    if (user.value.first_name) parts.push(user.value.first_name)
    if (user.value.last_name) parts.push(user.value.last_name)
    if (parts.length > 0) return parts.join(' ')
    return user.value.email?.split('@')[0] || 'User'
  })
  const userInitials = computed(() => {
    if (!user.value) return 'U'
    if (user.value.first_name && user.value.last_name) {
      return (user.value.first_name.charAt(0) + user.value.last_name.charAt(0)).toUpperCase()
    }
    if (user.value.first_name) {
      return user.value.first_name.charAt(0).toUpperCase()
    }
    return user.value.email?.split('@')[0]?.charAt(0).toUpperCase() || 'U'
  })

  // Profile completion
  const isProfileIncomplete = computed(() => {
    if (!user.value) return false

    const runtimeConfig = useRuntimeConfig()
    const phoneVerificationRequired = runtimeConfig.public.phoneVerificationRequired

    return (
      !user.value.first_name ||
      !user.value.last_name ||
      !user.value.phone ||
      !user.value.email ||
      (phoneVerificationRequired && user.value.phone && !user.value.is_phone_verified) ||
      !user.value.is_email_verified
    )
  })

  const getMissingFields = computed(() => {
    if (!user.value) return []

    const runtimeConfig = useRuntimeConfig()
    const phoneVerificationRequired = runtimeConfig.public.phoneVerificationRequired

    const missing: string[] = []

    if (!user.value.first_name) missing.push('first_name')
    if (!user.value.last_name) missing.push('last_name')
    if (!user.value.phone) missing.push('phone')
    if (!user.value.email) missing.push('email')
    if (phoneVerificationRequired && user.value.phone && !user.value.is_phone_verified)
      missing.push('phone_verification')
    if (user.value.email && !user.value.is_email_verified) missing.push('email_verification')

    return missing
  })

  return {
    // State
    user: readonly(user),
    isLoading: readonly(isLoading),
    favoriteIds: readonly(favoriteIds),
    likeIds: readonly(likeIds),
    likeIdsLoaded: readonly(likeIdsLoaded),

    // Computed
    isLoggedIn,
    userName,
    userInitials,
    isProfileIncomplete,
    getMissingFields,

    // Permissions
    hasPermission,

    // Actions
    setUser,
    clearUser,
    setLoading,
    setToken,
    getToken,
    clearToken,
    refreshToken,
    initializeAuth,

    // Favorites actions
    initFavoriteIds,
    resetFavoriteIds,
    isFavorite,
    addFavoriteId,
    removeFavoriteId,

    // Likes actions
    initLikeIds,
    resetLikeIds,
    isLiked,
    addLikeId,
    removeLikeId
  }
}
